import { Search } from "lucide-react";
import { useState } from "react";

import { ALL_FILTER, FilterSelect } from "@/components/filter-select";
import { PaginationFooter } from "@/components/pagination-footer";
import { Input } from "@/components/ui/input";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { type AdminAccountSummary, useAdminMcpAudit } from "@/lib/api";

import { McpAuditTable } from "./mcp-audit-table";

interface Props {
  accounts: AdminAccountSummary[];
  tools: string[];
}

const PAGE_SIZE = 25;
const OUTCOMES = ["ok", "denied", "error"];

/**
 * Server-side audit browser for MCP tool calls. Search is debounced; the three
 * selects and pagination re-run the query. Every filter change resets to page 1.
 */
export function McpAuditExplorer({ accounts, tools }: Props) {
  const [search, setSearch] = useState("");
  const [accountId, setAccountId] = useState(ALL_FILTER);
  const [tool, setTool] = useState(ALL_FILTER);
  const [outcome, setOutcome] = useState(ALL_FILTER);
  const [page, setPage] = useState(1);

  // Debounce the search box so we don't query on every keystroke; jump back to
  // the first page when the effective search changes.
  const debouncedSearch = useDebouncedValue(search, 300, () => setPage(1));

  const auditQuery = useAdminMcpAudit({
    search: debouncedSearch || undefined,
    accountId: accountId === ALL_FILTER ? undefined : accountId,
    tool: tool === ALL_FILTER ? undefined : tool,
    // The select only offers these three values, so the narrowing cast is sound.
    outcome: outcome === ALL_FILTER ? undefined : (outcome as "ok" | "denied" | "error"),
    page,
    pageSize: PAGE_SIZE,
  });

  const data = auditQuery.data ?? { items: [], total: 0, page: 1, page_size: PAGE_SIZE };
  const loading = auditQuery.isFetching;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <Search className="text-muted absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
          <Input
            placeholder="Search tool, person, or detail…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <FilterSelect
          value={accountId}
          onValueChange={(v) => {
            setAccountId(v);
            setPage(1);
          }}
          allLabel="All customers"
          options={accounts.map((account) => ({
            value: account.account_id,
            label: account.account_name,
          }))}
          className="w-full lg:w-48"
        />
        <FilterSelect
          value={tool}
          onValueChange={(v) => {
            setTool(v);
            setPage(1);
          }}
          allLabel="All tools"
          options={tools.map((t) => ({ value: t, label: t }))}
          className="w-full lg:w-44"
        />
        <FilterSelect
          value={outcome}
          onValueChange={(v) => {
            setOutcome(v);
            setPage(1);
          }}
          allLabel="All outcomes"
          options={OUTCOMES.map((o) => ({ value: o, label: o }))}
          className="w-full lg:w-36"
        />
      </div>

      <McpAuditTable entries={data.items} loading={loading} />

      <PaginationFooter
        page={data.page}
        pageSize={PAGE_SIZE}
        total={data.total}
        loading={loading}
        onPageChange={setPage}
      />
    </div>
  );
}
